import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, QrCode, FileText, Store, ArrowUpRight, Search, MapPin, Megaphone, UserPlus } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { StoreFooter } from "@/components/StoreFooter";
import { api, fileUrl } from "@/lib/api";
import { featuredUsers as fallbackMakers } from "@/lib/formSpecs";
function ProductCard({ p }) {
  const cover = p.images?.[0];
  return (
    <Link to={`/product/${p.id}`} className="product-card" data-testid={`store-product-${p.id}`}>
      <div className="product-art">
        {cover ? <img src={fileUrl(cover)} alt={p.name} /> : <span className="placeholder">✦</span>}
      </div>
      <div className="product-body">
        {p.verified
          ? <span className="verified"><ShieldCheck size={12} /> GI seller verified</span>
          : <span className="verified pending"><ShieldCheck size={12} /> Awaiting verification</span>}
        <h4>{p.name}</h4>
        <p>{p.gi_name}</p>
        <div className="price-row">
          <span className="now">₹{Number(p.final_price).toLocaleString("en-IN")}</span>
          {p.discount_pct > 0 && (
            <>
              <span className="mrp">₹{Number(p.mrp).toLocaleString("en-IN")}</span>
              <span className="off">{p.discount_pct}% off</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Storefront() {
  const [data, setData] = useState({ categories: [], notifications: [], featured_products: [], latest_products: [] });
  const [banners, setBanners] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [makers, setMakers] = useState([]);
  const [counters, setCounters] = useState({ authorised_users_total: 500, crafts: [] });
  const [homeAd, setHomeAd] = useState(null);
  const [banIdx, setBanIdx] = useState(0);
  const [slide, setSlide] = useState(0);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/home").then(r => setData(r.data)).catch(() => {});
    api.get("/banners").then(r => setBanners(r.data)).catch(() => {});
    api.get("/gallery").then(r => setGallery(r.data)).catch(() => {});
    api.get("/counters").then(r => setCounters(r.data)).catch(() => {});
    api.get("/featured-makers").then(r => setMakers(r.data)).catch(() => {});
    api.get("/home-ad").then(r => { if (r.data?.enabled && r.data?.media_path) setHomeAd(r.data); }).catch(() => {});
  }, []);
  const featuredUsers = makers.length ? makers : fallbackMakers;
  useEffect(() => {
    if (featuredUsers.length === 0) return;
    const t = setInterval(() => setSlide(s => (s + 1) % featuredUsers.length), 4500);
    return () => clearInterval(t);
  }, [featuredUsers.length]);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setBanIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  const filtered = useMemo(() => {
    const src = data.latest_products || [];
    const byCat = activeCat === "all" ? src : src.filter(p => p.category_id === activeCat);
    if (!search.trim()) return byCat;
    const q = search.toLowerCase();
    return byCat.filter(p => (p.name || "").toLowerCase().includes(q) || (p.gi_name || "").toLowerCase().includes(q));
  }, [data, activeCat, search]);
  const searching = Boolean(search.trim()) || activeCat !== "all";
  const current = featuredUsers[slide] || fallbackMakers[0];
  const submit = (e) => {
    e.preventDefault();
    const el = document.getElementById("latest");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="storefront">
      {/* Announcement */}
      <div className="gov-strip">
        <span className="tri" aria-hidden><span className="b saffron" /><span className="b white" /><span className="b green" /></span>
        <Link to="/login" className="become-au-link" data-testid="become-au-link"><UserPlus size={13} /> Become an Authorised User</Link>
        <Link to="/about">About us</Link>
        <Link to="/authorised-users">Authorised users</Link>
        <Link to="/contact">Contact</Link>
      </div>

      {/* Header */}
      <header className="store-header">
        <Link to="/" className="store-brand" data-testid="store-brand-link">
          <LogoMark />
          <span>GI Bharat<small>By True Vision Welfare Society</small></span>
        </Link>
        <form className="store-search" onSubmit={submit} data-testid="store-search">
          <select data-testid="store-search-category" value={activeCat} onChange={e => setActiveCat(e.target.value)}>
            <option value="all">All GI</option>
            {data.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input placeholder="Search GI products, artisans, or regions"
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="store-search-input" aria-label="Search"/>
          <button type="submit" data-testid="store-search-submit" aria-label="Search"><Search size={18} /></button>
        </form>
        <nav>
          <Link to="/about" data-testid="about-nav-link">About us<small>Our mission</small></Link>
          <Link to="/authorised-users" data-testid="au-nav-link">Authorised users<small>Directory</small></Link>
          <Link to="/contact" data-testid="contact-nav-link">Contact<small>Talk to us</small></Link>
          <Link to="/login" className="store-login" data-testid="store-login-link">Sign in <ArrowUpRight size={14} /></Link>
        </nav>
      </header>

      {/* Category strip */}
      <nav className="category-strip" data-testid="category-strip">
        <a href="#all" className={activeCat === "all" ? "active" : ""} onClick={e => { e.preventDefault(); setActiveCat("all"); }} data-testid="cat-all">All</a>
        {data.categories.map(c => (
          <a key={c.id} href={`#cat-${c.slug}`} className={activeCat === c.id ? "active" : ""}
            onClick={e => { e.preventDefault(); setActiveCat(c.id); }}
            data-testid={`cat-${c.slug}`}>
            {c.name}
          </a>
        ))}
      </nav>

      {/* Notifications strip */}
      {data.notifications.length > 0 && (
        <div className="notif-strip" data-testid="notifications-strip">
          <span className="tag"><Megaphone size={12} />NEWS</span>
          <div className="marquee-viewport">
            <div className="marquee">
              {[...data.notifications, ...data.notifications].map((n, i) => (
                <span key={`${n.id}-${i}`} data-testid={i < data.notifications.length ? `notif-${n.id}` : undefined}>
                  <b>{n.title}</b> — {n.message}
                  {n.link_url && <> · <a href={n.link_url} target="_blank" rel="noreferrer">Read more</a></>}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Banner carousel now lives inside the hero (right-side box) — no separate top section */}

      {/* Hero with BIG Join CTA */}
      <section className="store-hero">
        <div className="hero-copy">
          <p className="eyebrow">AUTHENTIC · ORIGIN-VERIFIED · TRUSTED CRAFT</p>
          <h1>India's finest GI<br /><em>stories, gathered.</em></h1>
          <p>Discover authorised makers with a verified connection to their place of origin, and shop directly from them.</p>
          <div className="hero-cta-row">
            <Link to="/login" className="hero-mega-btn" data-testid="hero-join-cta">
              <div>
                <small>ARE YOU AN ARTISAN OR FIRM?</small>
                Join the GI Network
              </div>
              <ArrowUpRight size={22} />
            </Link>
            <a className="hero-secondary-btn" href="#featured" data-testid="hero-explore-button">Explore products</a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-banner-box" data-testid="hero-banner-box">
            {banners.length > 0 ? (
              <>
                <img
                  src={fileUrl(banners[banIdx].image_path)}
                  alt={banners[banIdx].title || "True Vision banner"}
                  onError={e => { e.target.style.display = "none"; }}
                  data-testid={`hero-banner-image-${banIdx}`}
                />
                {(banners[banIdx].title || banners[banIdx].subtitle) && (
                  <div className="hero-banner-caption">
                    {banners[banIdx].title && <h3>{banners[banIdx].title}</h3>}
                    {banners[banIdx].subtitle && <p>{banners[banIdx].subtitle}</p>}
                  </div>
                )}
                {banners.length > 1 && (
                  <div className="hero-banner-dots">
                    {banners.map((_, i) => (
                      <button key={i} className={i === banIdx ? "active" : ""} onClick={() => setBanIdx(i)} aria-label={`Banner ${i + 1}`} data-testid={`hero-banner-dot-${i}`} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="placeholder-logo">
                <LogoMark size="mega" />
                <h4>GI Bharat · True Vision Welfare Society</h4>
                <p>GI authorised user services · Est. 2023</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Home advertisement (admin-editable, single slot) */}
      {homeAd && homeAd.media_path && (
        <section className="home-ad" data-testid="home-ad-section">
          <div className="home-ad-inner">
            <div className="home-ad-media">
              {homeAd.media_kind === "video"
                ? <video src={fileUrl(homeAd.media_path)} autoPlay muted loop playsInline data-testid="home-ad-video" />
                : <img src={fileUrl(homeAd.media_path)} alt={homeAd.headline || "Advertisement"} data-testid="home-ad-image" />}
            </div>
            <div className="home-ad-copy">
              <span className="home-ad-eyebrow">SPECIAL ANNOUNCEMENT</span>
              {homeAd.headline && <h2 data-testid="home-ad-headline">{homeAd.headline}</h2>}
              {homeAd.subtext && <p data-testid="home-ad-subtext">{homeAd.subtext}</p>}
              {homeAd.link_url && (
                homeAd.link_url.startsWith("http")
                  ? <a href={homeAd.link_url} target="_blank" rel="noopener noreferrer" className="primary-btn" data-testid="home-ad-link">{homeAd.link_label || "Learn more"} →</a>
                  : <Link to={homeAd.link_url} className="primary-btn" data-testid="home-ad-link">{homeAd.link_label || "Learn more"} →</Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Trust strip */}
      <section className="trust-strip">
        <span><ShieldCheck size={16} /> Verified authorised users</span>
        <span><QrCode size={16} /> Scan product authenticity</span>
        <span><FileText size={16} /> Assisted GI registration</span>
        <span><Store size={16} /> Buy direct from makers</span>
        <span><MapPin size={16} /> Crafted close to home</span>
      </section>

      {/* About Geographical Indications */}
      <section className="about-gi-band" data-testid="about-gi-section">
        <div className="about-gi-inner">
          <div className="about-gi-copy">
            <span className="about-gi-eyebrow"><ShieldCheck size={13} /> ABOUT GEOGRAPHICAL INDICATIONS (GI)</span>
            <h2>Products that carry the story of their place.</h2>
            <p>
              A Geographical Indication is an intellectual property right that identifies goods as originating from a
              particular country, region or locality — where a specific quality, reputation or characteristic of the
              product is essentially connected with that geographical origin.
            </p>
            <p>
              India protects such products under the <b>Geographical Indications of Goods (Registration and Protection) Act, 1999</b>,
              in force since 15 September 2003 and administered by the GI Registry at Chennai. Authorised users are the producers
              legally permitted to make and sell a registered GI product.
            </p>
            <div className="about-gi-cta">
              <Link to="/gi-information" className="primary-btn" data-testid="about-gi-more-info-btn">More information →</Link>
              <Link to="/login" className="secondary-btn" data-testid="about-gi-register-btn">Become an authorised user</Link>
            </div>
          </div>
          <aside className="about-gi-facts">
            <div><span>10 yrs</span><small>Protection term · renewable</small></div>
            <div><span>34</span><small>Classes of goods covered</small></div>
            <div><span>1999</span><small>Enacted · in force since 2003</small></div>
            <div><span>Chennai</span><small>Location of GI Registry</small></div>
          </aside>
        </div>
      </section>

      {/* Stats band (admin-editable) */}
      <section className="stats-band" data-testid="home-stats">
        <div className="inner">
          <div><div className="n" data-testid="home-total">{counters.authorised_users_total}+</div><div className="lbl">Authorised users</div></div>
          {(counters.crafts || []).slice(0, 3).map((c, i) => (
            <div key={i} data-testid={`home-craft-${i}`}>
              <div className="n">{c.count}+</div>
              <div className="lbl">{c.name}</div>
              <div className="desc">{c.description}</div>
            </div>
          ))}
          <div><Link to="/authorised-users" className="hero-secondary-btn" style={{ borderColor: "#f4d29b" }} data-testid="view-directory-link">View directory →</Link></div>
        </div>
        <p className="stats-nabard-note" data-testid="stats-nabard-note">
          Authorised users registered through <b>NABARD (The National Bank for Agriculture and Rural Development)</b> grant and support.
        </p>
      </section>


      {/* Photo gallery marquee (admin-uploaded) */}
      {gallery.length > 0 && (
        <section className="gallery-strip" data-testid="gallery-strip">
          <h3>Moments from the field</h3>
          <div className="gallery-track">
            {[...gallery, ...gallery].map((g, i) => (
              <div className="g-item" key={`${g.id}-${i}`} data-testid={`gallery-${g.id}-${i}`}>
                <img
                  src={fileUrl(g.image_path)}
                  alt={g.caption || ""}
                  onError={e => { e.target.parentElement.style.display = "none"; }}
                />
                {g.caption && <span>{g.caption}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Featured products */}
      {!searching && (
      <section className="section" id="featured">
        <div className="section-title">
          <h2>Featured GI products</h2>
          <a href="#latest" data-testid="see-all-link">See all latest →</a>
        </div>
        {data.featured_products.length ? (
          <div className="store-grid" data-testid="featured-grid">
            {data.featured_products.map(p => <ProductCard key={p.id} p={p} />)}
          </div>
        ) : (
          <div className="empty-state" data-testid="featured-empty">
            <Store size={26} />
            <h3>The catalogue is warming up</h3>
            <p>Authorised sellers are publishing their first listings. Come back soon.</p>
          </div>
        )}
      </section>
      )}

      {/* Makers carousel */}
      {!searching && featuredUsers.length > 0 && (
      <section className="section">
        <div className="section-title">
          <h2>Meet the makers</h2>
          <div className="carousel-controls">
            <button onClick={() => setSlide((slide + featuredUsers.length - 1) % featuredUsers.length)} data-testid="carousel-previous-button" aria-label="Previous">←</button>
            <span data-testid="carousel-counter">0{slide + 1} / 0{featuredUsers.length}</span>
            <button onClick={() => setSlide((slide + 1) % featuredUsers.length)} data-testid="carousel-next-button" aria-label="Next">→</button>
          </div>
        </div>
        <div className="maker-feature" data-testid="maker-feature">
          <div className="maker-portrait" style={{ background: current.color || "#0B6B3F" }}>
            {current.photo ? <img src={fileUrl(current.photo)} alt={current.name} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }} /> : <span>{current.initials || (current.name || "").slice(0, 2).toUpperCase()}</span>}
            <i>✦</i>
          </div>
          <div className="maker-copy">
            <p className="eyebrow">FEATURED AUTHORISED USER</p>
            <h3>{current.name}</h3>
            <p className="maker-place">{current.place} · {current.gi}</p>
            <p>{current.story || "Rooted in local knowledge and made with care, this authorised user brings a distinct regional story to every piece."}</p>
            <Link to="/login" className="arrow-link" data-testid="maker-view-link">Become an authorised user →</Link>
          </div>
        </div>
        <div className="maker-dots">
          {featuredUsers.map((item, index) => (
            <button key={item.name || index} className={index === slide ? "active" : ""} onClick={() => setSlide(index)} aria-label={`Show ${item.name}`} data-testid={`carousel-dot-${index}`} />
          ))}
        </div>
      </section>
      )}

      {/* Latest products / category filter */}
      <section className="section" id="latest">
        <div className="section-title">
          <h2>{activeCat === "all" ? "Latest arrivals" : (data.categories.find(c => c.id === activeCat)?.name || "Products")}</h2>
          {search && <span className="muted">Results for "{search}"</span>}
        </div>
        {filtered.length ? (
          <div className="store-grid" data-testid="latest-grid">
            {filtered.map(p => <ProductCard key={p.id} p={p} />)}
          </div>
        ) : (
          <div className="empty-state" data-testid="latest-empty">
            <Store size={26} />
            <h3>No products match your filters</h3>
            <p>Try a different category or clear your search.</p>
          </div>
        )}
      </section>

      {/* About */}
      <section className="section" id="about" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "start" }}>
        <div>
          <p className="eyebrow">ABOUT TRUE VISION</p>
          <h2>A more visible future for authentic Indian craft.</h2>
        </div>
        <div>
          <p>True Vision Welfare Society helps GI producers and authorised users turn recognition into opportunity. Since 2023, we have supported registration journeys, profile building, product visibility, and the trust that connects a customer to a real maker.</p>
          <p>Our work is simple: make origin easier to understand, protect the value of regional craft, and help responsible sellers reach the right buyers.</p>
          <Link to="/login" className="arrow-link" data-testid="about-join-link">Build your authorised profile →</Link>
        </div>
      </section>

      {/* Sell strip */}
      <section id="sell" style={{ background: "var(--india-blue)", color: "#fff", padding: "40px 5vw", textAlign: "center" }}>
        <p className="eyebrow" style={{ color: "var(--saffron)" }}>SELL WITH US</p>
        <h2 style={{ color: "#fff", maxWidth: 700, margin: "6px auto 12px", fontSize: "clamp(24px, 3vw, 34px)" }}>Are you a GI-authorised maker or firm?</h2>
        <p style={{ color: "#cfd8ec", maxWidth: 620, margin: "0 auto 22px" }}>List your products, get a verified public profile, generate authenticity QR codes, and receive buyer requests.</p>
        <button className="hero-mega-btn" onClick={() => navigate("/login")} data-testid="sell-join-cta">
          <div><small>NEW APPLICATION · MINUTES</small>Join the GI Network</div>
          <ArrowUpRight size={22} />
        </button>
      </section>

      <StoreFooter />
    </main>
  );
}
